import React, { useEffect, useState } from "react";
import axios from "axios";

const TABS = [
  { label: "In Progress", value: "inquireOn", icon: "🔄" },
  { label: "Completed", value: "inquireDone", icon: "✅" },
  { label: "Just Connections", value: "justConnection", icon: "🔗" },
];

const statusStyles = {
  justConnection: "bg-blue-100 text-blue-800",
  inquireOn: "bg-amber-100 text-amber-800",
  inquireDone: "bg-emerald-100 text-emerald-800",
};

const statusIcons = {
  justConnection: "🔵",
  inquireOn: "🟠",
  inquireDone: "🟢",
};

const Inquiry = () => {
  const [inquiries, setInquiries] = useState([]);
  const [activeTab, setActiveTab] = useState("inquireOn");
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [updatingId, setUpdatingId] = useState(null);

  useEffect(() => {
    const fetchInquiries = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/inquiry/all-inquiries`
        );
        setInquiries(response.data);
      } catch (error) {
        console.error("Error fetching inquiries:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchInquiries();
  }, []);

  const updateInquiryStatus = async (inquiryId) => {
    try {
      setUpdatingId(inquiryId);
      const data = JSON.stringify({
        status: "inquireDone",
      });

      const config = {
        method: "put",
        url: `${process.env.REACT_APP_API_URL}/inquiry/update-inquiry/${inquiryId}`,
        headers: {
          "Content-Type": "application/json",
        },
        data: data,
      };

      const response = await axios.request(config);

      setInquiries((prevInquiries) =>
        prevInquiries.map((inquiry) =>
          inquiry._id === inquiryId
            ? { ...inquiry, status: "inquireDone" }
            : inquiry
        )
      );

      return response.data;
    } catch (error) {
      console.error("Error updating inquiry status:", error);
      throw error;
    } finally {
      setUpdatingId(null);
    }
  };

  const handleStatusUpdate = async (inquiryId, currentStatus) => {
    if (currentStatus !== "inquireOn") {
      return;
    }

    try {
      await updateInquiryStatus(inquiryId);
    } catch (error) {
      console.error("Failed to update status:", error);
    }
  };

  const filteredInquiries = inquiries
    .filter((item) => item.status === activeTab)
    .filter(
      (item) =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.phone.includes(searchTerm)
    );

  const getStatusLabel = (statusValue) => {
    return TABS.find((tab) => tab.value === statusValue)?.label || statusValue;
  };

  return (
    <div className="min-h-screen p-6">
      <div className="">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <h1 className="text-xl font-bold text-gray-800">
              Customer Inquiries
            </h1>
            <p className="text-gray-500 mt-1">
              Manage and track customer communications
            </p>
          </div>

          <div className="relative w-full md:w-64">
            <input
              type="text"
              placeholder="Search inquiries..."
              className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <svg
              className="absolute left-3 top-2.5 h-5 w-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-8">
          {TABS.map((tab) => {
            const count = inquiries.filter(
              (item) => item.status === tab.value
            ).length;
            return (
              <button
                key={tab.value}
                onClick={() => setActiveTab(tab.value)}
                className={`flex items-center px-5 py-3 rounded-xl font-medium transition-all duration-200 ${
                  activeTab === tab.value
                    ? `${
                        statusStyles[tab.value]
                      } shadow-inner transform scale-[1.02]`
                    : "bg-white text-gray-600 hover:bg-gray-100 border border-gray-200"
                }`}
              >
                <span className="mr-2 text-lg">{tab.icon}</span>
                {tab.label}
                <span
                  className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                    activeTab === tab.value ? "bg-white" : "bg-gray-100"
                  }`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {TABS.map((tab) => {
            const count = inquiries.filter(
              (item) => item.status === tab.value
            ).length;
            return (
              <div
                key={`stat-${tab.value}`}
                className="bg-white p-4 rounded-xl shadow-sm border border-gray-100"
              >
                <div className="flex items-center">
                  <span
                    className={`text-2xl mr-3 p-2 rounded-full ${
                      statusStyles[tab.value]
                    }`}
                  >
                    {statusIcons[tab.value]}
                  </span>
                  <div>
                    <p className="text-sm text-gray-500">{tab.label}</p>
                    <p className="text-2xl font-bold">{count}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1, 2].map((_, idx) => (
              <div
                key={idx}
                className="p-4 border rounded-md shadow-sm space-y-4 animate-pulse bg-white"
              >
                <div className="flex items-center gap-3">
                  <div className="h-4 w-24 bg-gray-300 rounded"></div>{" "}
                  <div className="h-4 w-24 bg-gray-200 rounded"></div>{" "}
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <div className="h-4 w-40 bg-gray-200 rounded"></div>{" "}
                  <div className="h-4 w-28 bg-gray-200 rounded"></div>{" "}
                </div>
                <div className="h-12 bg-gray-200 rounded"></div>
                <div className="h-4 w-48 bg-gray-200 rounded"></div>{" "}
              </div>
            ))}
          </div>
        ) : filteredInquiries.length === 0 ? (
          <div className="bg-white p-8 rounded-xl shadow-sm text-center">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h3 className="mt-2 text-lg font-medium text-gray-900">
              No inquiries found
            </h3>
            <p className="mt-1 text-gray-500">
              {searchTerm
                ? "Try a different search term"
                : `No ${getStatusLabel(activeTab).toLowerCase()} inquiries`}
            </p>
          </div>
        ) : (
          <div className="grid gap-5">
            {filteredInquiries.map((inq) => (
              <div
                key={inq._id}
                className="p-5 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200 border border-gray-100"
              >
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-xl font-semibold text-gray-800">
                        {inq.name}
                      </h2>
                      <span
                        className={`text-xs px-2.5 py-0.5 rounded-full ${
                          statusStyles[inq.status]
                        }`}
                      >
                        {getStatusLabel(inq.status)}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
                      <a
                        href={`mailto:${inq.email}`}
                        className="flex items-center text-sm text-blue-600 hover:underline"
                      >
                        <svg
                          className="w-4 h-4 mr-1"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                          />
                        </svg>
                        {inq.email}
                      </a>
                      <a
                        href={`tel:${inq.phone}`}
                        className="flex items-center text-sm text-gray-600 hover:text-gray-800"
                      >
                        <svg
                          className="w-4 h-4 mr-1"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                          />
                        </svg>
                        {inq.phone}
                      </a>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {inq.status === "inquireOn" && (
                      <button
                        onClick={() => handleStatusUpdate(inq._id, inq.status)}
                        disabled={updatingId === inq._id}
                        className="px-3 py-1.5 text-sm bg-emerald-50 text-emerald-600 rounded-lg hover:bg-emerald-100 transition flex items-center justify-center min-w-[120px]"
                      >
                        {updatingId === inq._id ? (
                          <>
                            <svg
                              className="animate-spin -ml-1 mr-2 h-4 w-4 text-emerald-600"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                            Updating...
                          </>
                        ) : (
                          "Mark as Complete"
                        )}
                      </button>
                    )}
                  </div>
                </div>

                <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                  <p className="text-gray-700">{inq.message}</p>
                </div>

                <div className="mt-3 flex justify-between items-center text-xs text-gray-500">
                  <span>
                    Received: {new Date(inq.createdAt).toLocaleString()}
                  </span>
                  <div className="flex gap-2">
                    <button className="text-gray-400 hover:text-gray-600">
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                    </button>
                    <button className="text-gray-400 hover:text-gray-600">
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Inquiry;
